import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { DataTable, Column } from '@/components/DataTable';
import { SearchInput } from '@/components/SearchInput';
import { InstagramPostInsightsModal } from '@/components/facebook/InstagramPostInsightsModal';
import { IgAccountSwitchModal } from '@/components/facebook/IgAccountSwitchModal';
import { LoadErrorState } from '@/components/ListStates';
import { instagramService, IgMediaItem, IgMediaListResponse } from '@/services/instagram.service';
import { Plus, RefreshCw, ExternalLink, Copy, Heart, MessageCircle, Images, Film, Video, Image as ImageIcon, BarChart3, Camera } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/**
 * Everything published to the connected Instagram account, newest first.
 *
 * The nav's "Posts" entry used to open the composer, which left people asking
 * where the posts they had already published were listed. The composer now
 * lives at /instagram/posts/new and this is the landing page.
 *
 * Rows come from Meta's media edge, so posts made directly in the Instagram
 * app appear here too — not just the ones published from the CRM.
 */
export default function InstagramPosts() {
  const [media, setMedia] = useState<IgMediaListResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [insightsMedia, setInsightsMedia] = useState<IgMediaItem | null>(null);
  const [issue, setIssue] = useState<{ kind: 'not_connected' } | { kind: 'error'; message?: string } | null>(null);
  const [switching, setSwitching] = useState(false);

  const load = async (force = false) => {
    setRefreshing(true);
    setIssue(null);
    try {
      // 422 is expected (Meta connected, no Instagram account linked), so the interceptor must not toast it.
      setMedia(await instagramService.media(force, 50, undefined, [422]));
    } catch (e: any) {
      const body = e?.response?.data;
      if (body?.code === 'INSTAGRAM_NOT_CONNECTED') {
        setMedia(null);
        setIssue({ kind: 'not_connected' });
        return;
      }
      // Any other 422 skipped the interceptor too; with posts still on screen there's no inline error to carry it.
      if (e?.response?.status === 422 && media) toast.error(body?.message ?? 'Could not refresh Instagram posts');
      setIssue({ kind: 'error', message: body?.message });
    } finally {
      setRefreshing(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, []);

  const loadMore = async () => {
    const cursor = media?.paging?.cursors?.after;
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const next = await instagramService.media(false, 50, cursor);
      setMedia((prev) => (prev ? { ...next, data: [...prev.data, ...next.data] } : next));
    } finally {
      setLoadingMore(false);
    }
  };

  const rows = useMemo(() => {
    const all = media?.data ?? [];
    const needle = q.trim().toLowerCase();
    return all.filter((m) => {
      if (typeFilter !== 'ALL' && kindOf(m) !== typeFilter) return false;
      if (!needle) return true;
      return (m.caption ?? '').toLowerCase().includes(needle);
    });
  }, [media, q, typeFilter]);

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select and copy the link manually');
    }
  };

  const columns: Column<IgMediaItem>[] = [
    {
      key: 'post',
      header: 'Post',
      width: '2.4fr',
      render: (m) => {
        const thumb = m.imagekit_url || m.thumbnail_url || m.media_url;
        return (
          <button
            type="button"
            onClick={() => setInsightsMedia(m)}
            className="flex min-w-0 items-center gap-3 text-left"
            title="View insights"
          >
            <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-surface-alt">
              {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0">
              <div className="line-clamp-2 text-xs text-text-primary">
                {m.caption?.trim() || <span className="text-text-muted">No caption</span>}
              </div>
              <div className="mt-0.5 font-mono text-2xs text-text-muted">{m.id}</div>
            </div>
          </button>
        );
      },
    },
    {
      key: 'type',
      header: 'Type',
      width: '0.7fr',
      render: (m) => {
        const kind = kindOf(m);
        const Icon = KIND_ICON[kind];
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-muted">
            <Icon size={11} /> {KIND_LABEL[kind]}
          </span>
        );
      },
    },
    {
      key: 'published',
      header: 'Published',
      width: '0.9fr',
      render: (m) => (
        <span className="text-xs text-text-secondary">
          {m.timestamp
            ? new Date(m.timestamp).toLocaleString(undefined, {
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })
            : '—'}
        </span>
      ),
    },
    {
      key: 'engagement',
      header: 'Engagement',
      width: '0.8fr',
      render: (m) => (
        <div className="flex items-center gap-3 text-xs text-text-secondary">
          <span className="flex items-center gap-1"><Heart size={11} />{(m.like_count ?? 0).toLocaleString()}</span>
          <span className="flex items-center gap-1"><MessageCircle size={11} />{(m.comments_count ?? 0).toLocaleString()}</span>
        </div>
      ),
    },
    {
      key: 'link',
      header: 'Post URL',
      width: '1.5fr',
      render: (m) =>
        m.permalink ? (
          <div className="flex min-w-0 items-center gap-1.5">
            <a
              href={m.permalink}
              target="_blank"
              rel="noreferrer"
              className="truncate text-xs text-primary hover:underline"
              title={m.permalink}
            >
              {m.permalink.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
            <button
              type="button"
              onClick={() => copyLink(m.permalink!)}
              className="shrink-0 rounded-md p-1 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
              title="Copy link"
            >
              <Copy size={12} />
            </button>
            <a
              href={m.permalink}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-md p-1 text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
              title="Open on Instagram"
            >
              <ExternalLink size={12} />
            </a>
          </div>
        ) : (
          <span className="text-2xs text-text-muted">No permalink</span>
        ),
    },
  ];

  return (
    <>
      <Topbar
        title="Instagram Posts"
        breadcrumb="Instagram / Posts"
        description="Everything published to the connected account, newest first. Click a row for its insights."
        right={
          <div className="flex items-center gap-2">
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50"
              onClick={() => load(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <Link to="/instagram/posts/new">
              <Button iconLeft={<Plus size={14} />}>New post</Button>
            </Link>
          </div>
        }
      />

      <div className="w-full space-y-4 px-7 py-7">
        {issue?.kind !== 'not_connected' && (
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search captions…" />
          <div className="flex items-center gap-1">
            {(['ALL', 'IMAGE', 'CAROUSEL', 'REEL', 'VIDEO'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTypeFilter(k)}
                className={clsx(
                  'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                  typeFilter === k
                    ? 'bg-primary-soft text-primary'
                    : 'text-text-muted hover:bg-surface-alt hover:text-text-primary',
                )}
              >
                {k === 'ALL' ? 'All' : KIND_LABEL[k]}
              </button>
            ))}
          </div>
          {media && (
            <span className="ml-auto text-2xs text-text-muted">
              {rows.length} of {media.data.length} shown
            </span>
          )}
        </div>
        )}

        {!media && issue?.kind === 'not_connected' ? (
          <EmptyState
            icon={<Camera size={28} />}
            title="No Instagram account connected"
            description="Your Meta account is connected, but no Instagram professional account is linked to this workspace yet. Pick one to see its posts here — only accounts linked to one of your Facebook Pages are listed."
            action={
              <>
                <Button iconLeft={<Camera size={14} />} onClick={() => setSwitching(true)}>Connect Instagram account</Button>
                <Link to="/facebook/accounts">
                  <Button variant="secondary">Manage Meta accounts</Button>
                </Link>
              </>
            }
          />
        ) : !media && issue?.kind === 'error' ? (
          <LoadErrorState what="your Instagram posts" message={issue.message} onRetry={() => void load(false)} />
        ) : !media && (
          <Card><div className="skeleton h-40 rounded-lg" /></Card>
        )}

        {media && rows.length === 0 && (
          <EmptyState
            icon={<ImageIcon size={40} className="text-text-muted" />}
            title={media.data.length === 0 ? 'No posts yet' : 'Nothing matches your filters'}
            description={
              media.data.length === 0
                ? 'Publish your first post from the CRM — image, carousel, video, reel or story — and it will show up here with its Instagram link.'
                : 'Try a different search term or post type, or clear the filters to see every post.'
            }
            action={
              media.data.length === 0 ? (
                <Link to="/instagram/posts/new">
                  <Button iconLeft={<Plus size={14} />}>New post</Button>
                </Link>
              ) : (
                <Button variant="secondary" onClick={() => { setQ(''); setTypeFilter('ALL'); }}>Clear filters</Button>
              )
            }
          />
        )}

        {media && rows.length > 0 && (
          <>
            <Card padded={false}>
              <DataTable columns={columns} rows={rows} stickyHeader />
            </Card>
            {media.paging?.next && (
              <div className="flex justify-center">
                <Button variant="secondary" loading={loadingMore} onClick={loadMore}>
                  Load more
                </Button>
              </div>
            )}
            <p className="flex items-center gap-1.5 text-2xs text-text-muted">
              <BarChart3 size={12} />
              Reach, saves and other per-post metrics open in the insights dialog.
            </p>
          </>
        )}
      </div>

      <IgAccountSwitchModal
        open={switching}
        currentId={null}
        onClose={() => setSwitching(false)}
        onSwitched={() => { setSwitching(false); void load(true); }}
      />

      <InstagramPostInsightsModal
        open={!!insightsMedia}
        onClose={() => setInsightsMedia(null)}
        media={insightsMedia}
        onDeleted={(deletedId) => {
          setMedia((prev) => (prev ? { ...prev, data: prev.data.filter((m) => m.id !== deletedId) } : prev));
          load(true).catch(() => undefined);
        }}
      />
    </>
  );
}

type Kind = 'IMAGE' | 'CAROUSEL' | 'REEL' | 'VIDEO';

/** Meta reports reels as VIDEO + media_product_type REELS, so normalise. */
function kindOf(m: IgMediaItem): Kind {
  if (m.media_type === 'CAROUSEL_ALBUM') return 'CAROUSEL';
  if (m.media_product_type === 'REELS' || m.media_type === 'REELS') return 'REEL';
  if (m.media_type === 'VIDEO') return 'VIDEO';
  return 'IMAGE';
}

const KIND_LABEL: Record<Kind, string> = {
  IMAGE: 'Image', CAROUSEL: 'Carousel', REEL: 'Reel', VIDEO: 'Video',
};

const KIND_ICON: Record<Kind, React.ComponentType<{ size?: number; className?: string }>> = {
  IMAGE: ImageIcon, CAROUSEL: Images, REEL: Film, VIDEO: Video,
};
